/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { SHIFTIQ_API, getAuthHeaders } from "../../api/client";
import { Icon, LoadingPulse } from "../../ui";

import {
  RECIPE_BRANDS,
  EMPTY_RECIPE_FORM,
  EMPTY_ALIAS_FORM,
  recipeBrandLabel,
  recipeBrandBadgeStyle,
  recipesApiError,
  formatRecipeDate,
} from "./helpers";

export function RecipesPage({ lang }) {
  const [activeTab, setActiveTab] = useState("master");
  const [recipes, setRecipes] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [inactiveAliases, setInactiveAliases] = useState([]);
  const [inactiveCount, setInactiveCount] = useState(0);
  const [showInactiveAliases, setShowInactiveAliases] = useState(false);
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [loadingAliases, setLoadingAliases] = useState(true);
  const [masterError, setMasterError] = useState(null);
  const [aliasesError, setAliasesError] = useState(null);
  const [unmappedError, setUnmappedError] = useState(null);
  const [recipeModal, setRecipeModal] = useState(null);
  const [aliasModal, setAliasModal] = useState(null);
  const [recipeForm, setRecipeForm] = useState(EMPTY_RECIPE_FORM);
  const [aliasForm, setAliasForm] = useState(EMPTY_ALIAS_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteRecipeId, setDeleteRecipeId] = useState(null);
  const [deleteAliasId, setDeleteAliasId] = useState(null);
  const [deleteInactiveAlias, setDeleteInactiveAlias] = useState(null);
  const [recipeDeleteErrors, setRecipeDeleteErrors] = useState({});
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const t = (en, gr) => (lang === "gr" ? gr : en);

  function showToast(message) {
    setToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2800);
  }

  const loadRecipes = useCallback(() => {
    setLoadingMaster(true);
    setMasterError(null);
    fetch(`${SHIFTIQ_API}/api/kitchen/recipe-master`, { headers: getAuthHeaders() })
      .then(async (r) => {
        if (!r.ok) throw new Error(await recipesApiError(r, "Failed to load recipes"));
        return r.json();
      })
      .then((data) => setRecipes(Array.isArray(data) ? data : []))
      .catch((err) => {
        setMasterError(err?.message || t("Failed to load recipes", "Αποτυχία φόρτωσης συνταγών"));
        setRecipes([]);
      })
      .finally(() => setLoadingMaster(false));
  }, [lang]);

  const loadAliases = useCallback(() => {
    setLoadingAliases(true);
    setAliasesError(null);
    setUnmappedError(null);
    setShowInactiveAliases(false);
    Promise.all([
      fetch(`${SHIFTIQ_API}/api/kitchen/recipe-aliases`, { headers: getAuthHeaders() }),
      fetch(`${SHIFTIQ_API}/api/kitchen/unmapped-programs`, { headers: getAuthHeaders() }),
      fetch(`${SHIFTIQ_API}/api/kitchen/recipe-aliases/inactive`, { headers: getAuthHeaders() }),
    ])
      .then(async ([aliasRes, unmappedRes, inactiveRes]) => {
        if (!aliasRes.ok) throw new Error(await recipesApiError(aliasRes, "Failed to load aliases"));
        if (!unmappedRes.ok) throw new Error(await recipesApiError(unmappedRes, "Failed to load unmapped programs"));
        if (!inactiveRes.ok) throw new Error(await recipesApiError(inactiveRes, "Failed to load inactive aliases"));
        const [aliasData, unmappedData, inactiveData] = await Promise.all([
          aliasRes.json(),
          unmappedRes.json(),
          inactiveRes.json(),
        ]);
        setAliases(Array.isArray(aliasData) ? aliasData : []);
        setUnmapped(Array.isArray(unmappedData) ? unmappedData : []);
        const inactiveList = Array.isArray(inactiveData?.aliases) ? inactiveData.aliases : [];
        setInactiveAliases(inactiveList);
        setInactiveCount(
          typeof inactiveData?.count === "number" ? inactiveData.count : inactiveList.length
        );
      })
      .catch((err) => {
        const msg = err?.message || t("Failed to load aliases", "Αποτυχία φόρτωσης aliases");
        setAliasesError(msg);
        setUnmappedError(msg);
        setAliases([]);
        setUnmapped([]);
        setInactiveAliases([]);
        setInactiveCount(0);
      })
      .finally(() => setLoadingAliases(false));
  }, [lang]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  useEffect(() => {
    if (activeTab === "aliases") loadAliases();
  }, [activeTab, loadAliases]);

  function openAddRecipe() {
    setFormError("");
    setRecipeForm(EMPTY_RECIPE_FORM);
    setRecipeModal("add");
  }

  function openEditRecipe(row) {
    setFormError("");
    setRecipeForm({
      canonical_name: row.canonical_name || "",
      brand: row.brand || "proteingarden",
      vessel: row.vessel || "",
      raw_weight_grams: row.raw_weight_grams ?? "",
      cooked_weight_grams: row.cooked_weight_grams ?? "",
    });
    setRecipeModal(row.id);
  }

  function openAddAlias(prefillName = "") {
    setFormError("");
    setAliasForm({
      ...EMPTY_ALIAS_FORM,
      alias_name: prefillName || "",
    });
    setAliasModal("add");
    setActiveTab("aliases");
    if (recipes.length === 0) loadRecipes();
  }

  function openEditAlias(row) {
    setFormError("");
    setAliasForm({
      alias_name: row.alias_name || "",
      recipe_master_id: row.recipe_master_id != null ? String(row.recipe_master_id) : "",
      is_reheat: Boolean(row.is_reheat),
    });
    setAliasModal(row.id);
    if (recipes.length === 0) loadRecipes();
  }

  async function saveRecipe() {
    const name = recipeForm.canonical_name.trim();
    if (!name) {
      setFormError(t("Canonical name is required", "Απαιτείται canonical name"));
      return;
    }
    if (recipeForm.cooked_weight_grams === "" || recipeForm.cooked_weight_grams == null) {
      setFormError(t("Cooked weight is required", "Απαιτείται cooked weight"));
      return;
    }
    const payload = {
      canonical_name: name,
      brand: recipeForm.brand,
      vessel: recipeForm.vessel.trim() || null,
      raw_weight_grams: recipeForm.raw_weight_grams === "" ? null : Number(recipeForm.raw_weight_grams),
      cooked_weight_grams: Number(recipeForm.cooked_weight_grams),
    };
    setSaving(true);
    setFormError("");
    try {
      const isEdit = recipeModal !== "add";
      const res = await fetch(
        isEdit
          ? `${SHIFTIQ_API}/api/kitchen/recipe-master/${recipeModal}`
          : `${SHIFTIQ_API}/api/kitchen/recipe-master`,
        {
          method: isEdit ? "PUT" : "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(await recipesApiError(res, t("Save failed", "Αποτυχία αποθήκευσης")));
      setRecipeModal(null);
      showToast(t("Recipe saved", "Η συνταγή αποθηκεύτηκε"));
      loadRecipes();
      if (activeTab === "aliases") loadAliases();
    } catch (err) {
      setFormError(err?.message || t("Save failed", "Αποτυχία αποθήκευσης"));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDeleteRecipe(id) {
    setSaving(true);
    setRecipeDeleteErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/kitchen/recipe-master/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (res.status === 400) {
        const detail = await recipesApiError(res, t("Delete failed", "Αποτυχία διαγραφής"));
        const match = detail.match(/Cannot delete:\s*(\d+)\s*alias/i);
        const count = match ? match[1] : "some";
        setRecipeDeleteErrors((prev) => ({
          ...prev,
          [id]: t(
            `Cannot delete — ${count} aliases still reference this recipe. Remove the aliases first, then delete the recipe.`,
            `Δεν είναι δυνατή η διαγραφή — ${count} aliases αναφέρονται ακόμα σε αυτή τη συνταγή. Αφαιρέστε πρώτα τα aliases και μετά διαγράψτε τη συνταγή.`
          ),
        }));
        setDeleteRecipeId(null);
        return;
      }
      if (!res.ok) throw new Error(await recipesApiError(res, t("Delete failed", "Αποτυχία διαγραφής")));
      setDeleteRecipeId(null);
      showToast(t("Recipe deleted", "Η συνταγή διαγράφηκε"));
      loadRecipes();
    } catch (err) {
      showToast(err?.message || t("Delete failed", "Αποτυχία διαγραφής"));
    } finally {
      setSaving(false);
    }
  }

  async function saveAlias() {
    const aliasName = aliasForm.alias_name.trim();
    if (!aliasName) {
      setFormError(t("Alias name is required", "Απαιτείται alias name"));
      return;
    }
    if (!aliasForm.is_reheat && !aliasForm.recipe_master_id) {
      setFormError(t("Canonical recipe is required unless reheat", "Απαιτείται canonical recipe εκτός αν είναι reheat"));
      return;
    }
    const payload = {
      alias_name: aliasName,
      is_reheat: Boolean(aliasForm.is_reheat),
      recipe_master_id: aliasForm.is_reheat
        ? aliasForm.recipe_master_id
          ? Number(aliasForm.recipe_master_id)
          : null
        : Number(aliasForm.recipe_master_id),
    };
    setSaving(true);
    setFormError("");
    try {
      const isEdit = aliasModal !== "add";
      const res = await fetch(
        isEdit
          ? `${SHIFTIQ_API}/api/kitchen/recipe-aliases/${aliasModal}`
          : `${SHIFTIQ_API}/api/kitchen/recipe-aliases`,
        {
          method: isEdit ? "PUT" : "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error(await recipesApiError(res, t("Save failed", "Αποτυχία αποθήκευσης")));
      setAliasModal(null);
      showToast(t("Alias saved", "Το alias αποθηκεύτηκε"));
      loadAliases();
    } catch (err) {
      setFormError(err?.message || t("Save failed", "Αποτυχία αποθήκευσης"));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDeleteAlias(id) {
    setSaving(true);
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/kitchen/recipe-aliases/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(await recipesApiError(res, t("Delete failed", "Αποτυχία διαγραφής")));
      setDeleteAliasId(null);
      showToast(t("Alias deleted", "Το alias διαγράφηκε"));
      loadAliases();
    } catch (err) {
      showToast(err?.message || t("Delete failed", "Αποτυχία διαγραφής"));
    } finally {
      setSaving(false);
    }
  }

  async function confirmDeleteInactiveAlias(id) {
    setSaving(true);
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/kitchen/recipe-aliases/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(await recipesApiError(res, t("Delete failed", "Αποτυχία διαγραφής")));
      setDeleteInactiveAlias(null);
      showToast(t("Alias removed", "Το alias αφαιρέθηκε"));
      loadAliases();
    } catch (err) {
      showToast(err?.message || t("Delete failed", "Αποτυχία διαγραφής"));
    } finally {
      setSaving(false);
    }
  }

  const recipeModalTitle =
    recipeModal === "add"
      ? t("Add Recipe", "Προσθήκη Συνταγής")
      : t("Edit Recipe", "Επεξεργασία Συνταγής");

  const aliasModalTitle =
    aliasModal === "add"
      ? t("Add Alias", "Προσθήκη Alias")
      : t("Edit Alias", "Επεξεργασία Alias");

  return (
    <div className="anim-fade">
      {toast && (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3000,
            background: "var(--surface)",
            color: "var(--text-primary)",
            border: "1.5px solid var(--border)",
            borderRadius: 10,
            padding: "10px 16px",
            fontSize: 13,
            fontWeight: 700,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            whiteSpace: "nowrap",
          }}
        >
          {toast}
        </div>
      )}

      <div className="procureiq-tabs">
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "master" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("master")}
        >
          {t("Recipe Master", "Recipe Master")}
        </button>
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "aliases" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("aliases")}
        >
          {t("Alias Mapping", "Alias Mapping")}
        </button>
      </div>

      {activeTab === "master" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
              {t(
                "Canonical recipes used for production weight calculations.",
                "Κανονικές συνταγές για υπολογισμούς παραγωγής."
              )}
            </p>
            <button className="btn-base btn-green" onClick={openAddRecipe}>
              <Icon name="plus" size={14} />
              {t("Add Recipe", "Προσθήκη Συνταγής")}
            </button>
          </div>

          {loadingMaster && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <LoadingPulse size={22} />
            </div>
          )}

          {!loadingMaster && masterError && (
            <div style={{ marginBottom: 14, background: "#FEF0ED", border: "1px solid #FACFC8", borderRadius: 12, padding: "12px 14px", fontSize: 13, color: "var(--danger)", fontWeight: 600 }}>
              {masterError}
            </div>
          )}

          {!loadingMaster && !masterError && (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t("Canonical Name", "Canonical Name")}</th>
                    <th>{t("Brand", "Brand")}</th>
                    <th>{t("Vessel", "Vessel")}</th>
                    <th style={{ textAlign: "right" }}>{t("Raw Weight (g)", "Raw (g)")}</th>
                    <th style={{ textAlign: "right" }}>{t("Cooked Weight (g)", "Cooked (g)")}</th>
                    <th>{t("Last used", "Last used")}</th>
                    <th style={{ width: 120 }} />
                  </tr>
                </thead>
                <tbody>
                  {recipes.map((row) => (
                    <React.Fragment key={row.id}>
                      <tr>
                        <td style={{ fontWeight: 700, color: "var(--text-primary)" }}>{row.canonical_name}</td>
                        <td>
                          <span className="procureiq-badge" style={recipeBrandBadgeStyle(row.brand)}>
                            {recipeBrandLabel(row.brand)}
                          </span>
                        </td>
                        <td style={{ color: "var(--text-secondary)" }}>{row.vessel || "—"}</td>
                        <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                          {row.raw_weight_grams != null ? row.raw_weight_grams : "—"}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 700, color: "var(--green)", fontVariantNumeric: "tabular-nums" }}>
                          {row.cooked_weight_grams != null ? row.cooked_weight_grams : "—"}
                        </td>
                        <td style={{ color: "var(--text-secondary)", fontVariantNumeric: "tabular-nums" }}>
                          {row.last_used_date ? formatRecipeDate(row.last_used_date, lang) : t("Never", "Ποτέ")}
                        </td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <button className="btn-base btn-outline btn-small" style={{ marginRight: 6 }} onClick={() => openEditRecipe(row)}>
                            {t("Edit", "Επεξεργασία")}
                          </button>
                          <button className="btn-base btn-outline btn-small" onClick={() => setDeleteRecipeId(row.id)}>
                            {t("Delete", "Διαγραφή")}
                          </button>
                        </td>
                      </tr>
                      {recipeDeleteErrors[row.id] && (
                        <tr>
                          <td colSpan={7} style={{ padding: "0 12px 10px", borderBottom: "1px solid var(--border)" }}>
                            <div style={{ background: "#FEF0ED", border: "1px solid #FACFC8", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "var(--danger)", fontWeight: 600 }}>
                              {recipeDeleteErrors[row.id]}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {recipes.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)", fontWeight: 600 }}>
                        {t("No recipes yet", "Δεν υπάρχουν συνταγές")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "aliases" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
              {t(
                "Map oven program names to canonical recipes.",
                "Σύνδεση ονομάτων προγραμμάτων φούρνου με canonical recipes."
              )}
            </p>
            <button className="btn-base btn-green" onClick={() => openAddAlias()}>
              <Icon name="plus" size={14} />
              {t("Add Alias", "Προσθήκη Alias")}
            </button>
          </div>

          {loadingAliases && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <LoadingPulse size={22} />
            </div>
          )}

          {!loadingAliases && aliasesError && (
            <div style={{ marginBottom: 14, background: "#FEF0ED", border: "1px solid #FACFC8", borderRadius: 12, padding: "12px 14px", fontSize: 13, color: "var(--danger)", fontWeight: 600 }}>
              {aliasesError}
            </div>
          )}

          {!loadingAliases && !aliasesError && (
            <>
              <div className="admin-table-wrap" style={{ marginBottom: 20 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>{t("Alias Name", "Alias Name")}</th>
                      <th>{t("Canonical Recipe", "Canonical Recipe")}</th>
                      <th>{t("Brand", "Brand")}</th>
                      <th>{t("Is Reheat", "Reheat")}</th>
                      <th style={{ textAlign: "right" }}>{t("Times Seen", "Times Seen")}</th>
                      <th style={{ width: 120 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {aliases.map((row) => (
                      <tr key={row.id}>
                        <td style={{ fontWeight: 700, color: "var(--text-primary)" }}>{row.alias_name}</td>
                        <td style={{ color: "var(--text-secondary)" }}>{row.canonical_name || (row.is_reheat ? "—" : t("Unlinked", "Χωρίς σύνδεση"))}</td>
                        <td>
                          {row.brand ? (
                            <span className="procureiq-badge" style={recipeBrandBadgeStyle(row.brand)}>
                              {recipeBrandLabel(row.brand)}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <span
                            className="procureiq-badge"
                            style={row.is_reheat ? { background: "#FEF3C7", color: "#92400E" } : { background: "#F3F4F6", color: "#4B5563" }}
                          >
                            {row.is_reheat ? t("Yes", "Ναι") : t("No", "Όχι")}
                          </span>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{row.times_seen ?? 0}</td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <button className="btn-base btn-outline btn-small" style={{ marginRight: 6 }} onClick={() => openEditAlias(row)}>
                            {t("Edit", "Επεξεργασία")}
                          </button>
                          <button className="btn-base btn-outline btn-small" onClick={() => setDeleteAliasId(row.id)}>
                            {t("Delete", "Διαγραφή")}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {aliases.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)", fontWeight: 600 }}>
                          {t("No aliases yet", "Δεν υπάρχουν aliases")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {inactiveCount > 0 && (
                <div className="card-box" style={{ padding: "16px 18px", marginBottom: 20 }}>
                  <button
                    type="button"
                    className="btn-base btn-outline"
                    onClick={() => setShowInactiveAliases((v) => !v)}
                    style={{ marginBottom: showInactiveAliases ? 14 : 0 }}
                  >
                    {showInactiveAliases
                      ? t("Hide inactive aliases", "Απόκρυψη inactive aliases")
                      : t(`Show inactive aliases (${inactiveCount})`, `Εμφάνιση inactive aliases (${inactiveCount})`)}
                  </button>

                  {showInactiveAliases && (
                    <>
                      <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text-primary)", marginBottom: 4 }}>
                        {t("Inactive aliases", "Inactive aliases")}
                      </div>
                      <p style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 14, lineHeight: 1.5 }}>
                        {t(
                          "These aliases have not appeared in oven data in the last 30 days and may be safe to remove.",
                          "Αυτά τα aliases δεν εμφανίστηκαν στα δεδομένα φούρνου τις τελευταίες 30 ημέρες και μπορεί να είναι ασφαλές να αφαιρεθούν."
                        )}
                      </p>
                      <div className="admin-table-wrap">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>{t("Alias Name", "Alias Name")}</th>
                              <th>{t("Canonical Recipe", "Canonical Recipe")}</th>
                              <th>{t("Is Reheat", "Reheat")}</th>
                              <th>{t("Last Seen", "Last Seen")}</th>
                              <th style={{ textAlign: "right" }}>{t("Times Seen", "Times Seen")}</th>
                              <th style={{ width: 90 }} />
                            </tr>
                          </thead>
                          <tbody>
                            {inactiveAliases.map((row) => (
                              <tr key={row.id}>
                                <td style={{ fontWeight: 700, color: "var(--text-primary)" }}>{row.alias_name}</td>
                                <td style={{ color: "var(--text-secondary)" }}>
                                  {row.canonical_name || (row.is_reheat ? "—" : t("Unlinked", "Χωρίς σύνδεση"))}
                                </td>
                                <td>
                                  <span
                                    className="procureiq-badge"
                                    style={row.is_reheat ? { background: "#FEF3C7", color: "#92400E" } : { background: "#F3F4F6", color: "#4B5563" }}
                                  >
                                    {row.is_reheat ? t("Yes", "Ναι") : t("No", "Όχι")}
                                  </span>
                                </td>
                                <td style={{ fontVariantNumeric: "tabular-nums", color: "var(--text-secondary)" }}>
                                  {row.last_seen ? formatRecipeDate(row.last_seen, lang) : t("Never", "Ποτέ")}
                                </td>
                                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                  {row.times_seen_all_time ?? 0}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  <button
                                    className="btn-base btn-outline btn-small"
                                    onClick={() => setDeleteInactiveAlias({ id: row.id, alias_name: row.alias_name })}
                                  >
                                    {t("Delete", "Διαγραφή")}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="card-box" style={{ padding: "16px 18px" }}>
                <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text-primary)", marginBottom: 4 }}>
                  {t("Unmapped oven programs", "Unmapped oven programs")}
                </div>
                <p style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 14, lineHeight: 1.5 }}>
                  {t(
                    "These program names appear in oven data but have no alias mapping yet",
                    "Αυτά τα ονόματα εμφανίζονται στα δεδομένα φούρνου χωρίς alias mapping"
                  )}
                </p>

                {unmappedError && (
                  <div style={{ marginBottom: 10, fontSize: 13, color: "var(--danger)", fontWeight: 600 }}>{unmappedError}</div>
                )}

                <div className="admin-table-wrap">
                  <table className="admin-table" style={{ minWidth: 520 }}>
                    <thead>
                      <tr>
                        <th>{t("Program name", "Όνομα προγράμματος")}</th>
                        <th style={{ textAlign: "right" }}>{t("Times seen (30d)", "Εμφανίσεις (30ημ.)")}</th>
                        <th style={{ width: 110 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {unmapped.map((row) => (
                        <tr key={row.item_name}>
                          <td style={{ fontWeight: 700, color: "var(--text-primary)" }}>{row.item_name}</td>
                          <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{row.times_seen}</td>
                          <td style={{ textAlign: "right" }}>
                            <button className="btn-base btn-outline btn-small" onClick={() => openAddAlias(row.item_name)}>
                              {t("Add Alias", "Προσθήκη Alias")}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {unmapped.length === 0 && (
                        <tr>
                          <td colSpan={3} style={{ textAlign: "center", padding: 24, color: "var(--text-muted)", fontWeight: 600 }}>
                            {t("All programs mapped", "Όλα τα προγράμματα έχουν mapping")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {recipeModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setRecipeModal(null)}>
          <div className="modal-box" style={{ maxWidth: 520 }}>
            <div className="modal-handle" />
            <div className="modal-title">{recipeModalTitle}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <label className="form-label">{t("Canonical Name", "Canonical Name")} *</label>
                <input
                  className="form-input"
                  value={recipeForm.canonical_name}
                  onChange={(e) => setRecipeForm((prev) => ({ ...prev, canonical_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="form-label">{t("Brand", "Brand")} *</label>
                <select
                  className="form-select"
                  value={recipeForm.brand}
                  onChange={(e) => setRecipeForm((prev) => ({ ...prev, brand: e.target.value }))}
                >
                  {RECIPE_BRANDS.map((b) => (
                    <option key={b} value={b}>{recipeBrandLabel(b)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">{t("Vessel", "Vessel")}</label>
                <input
                  className="form-input"
                  value={recipeForm.vessel}
                  onChange={(e) => setRecipeForm((prev) => ({ ...prev, vessel: e.target.value }))}
                />
              </div>
              <div>
                <label className="form-label">{t("Raw Weight (g)", "Raw Weight (g)")}</label>
                <input
                  type="number"
                  step="0.1"
                  className="form-input"
                  value={recipeForm.raw_weight_grams}
                  onChange={(e) => setRecipeForm((prev) => ({ ...prev, raw_weight_grams: e.target.value }))}
                />
              </div>
              <div>
                <label className="form-label">{t("Cooked Weight (g)", "Cooked Weight (g)")} *</label>
                <input
                  type="number"
                  step="0.1"
                  className="form-input"
                  value={recipeForm.cooked_weight_grams}
                  onChange={(e) => setRecipeForm((prev) => ({ ...prev, cooked_weight_grams: e.target.value }))}
                />
              </div>
            </div>
            {formError && (
              <div style={{ marginTop: 12, background: "#FEF0ED", border: "1px solid #FACFC8", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "var(--danger)", fontWeight: 600 }}>
                {formError}
              </div>
            )}
            <div style={{ display: "flex", gap: 9, marginTop: 20 }}>
              <button className="btn-base btn-green" style={{ flex: 1, justifyContent: "center" }} onClick={saveRecipe} disabled={saving}>
                {saving ? t("Saving...", "Αποθήκευση...") : t("Save", "Αποθήκευση")}
              </button>
              <button className="btn-base btn-outline" onClick={() => { setRecipeModal(null); setFormError(""); }}>
                {t("Cancel", "Ακύρωση")}
              </button>
            </div>
          </div>
        </div>
      )}

      {aliasModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setAliasModal(null)}>
          <div className="modal-box" style={{ maxWidth: 520 }}>
            <div className="modal-handle" />
            <div className="modal-title">{aliasModalTitle}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label className="form-label">{t("Alias Name", "Alias Name")} *</label>
                <input
                  className="form-input"
                  value={aliasForm.alias_name}
                  onChange={(e) => setAliasForm((prev) => ({ ...prev, alias_name: e.target.value }))}
                />
              </div>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "var(--text-secondary)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={aliasForm.is_reheat}
                  onChange={(e) => setAliasForm((prev) => ({
                    ...prev,
                    is_reheat: e.target.checked,
                    recipe_master_id: e.target.checked ? "" : prev.recipe_master_id,
                  }))}
                />
                {t("Is Reheat", "Is Reheat")}
              </label>
              <div>
                <label className="form-label">
                  {t("Canonical Recipe", "Canonical Recipe")}
                  {!aliasForm.is_reheat ? " *" : ""}
                </label>
                <select
                  className="form-select"
                  value={aliasForm.recipe_master_id}
                  disabled={aliasForm.is_reheat}
                  style={aliasForm.is_reheat ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                  onChange={(e) => setAliasForm((prev) => ({ ...prev, recipe_master_id: e.target.value }))}
                >
                  <option value="">{t("Select recipe...", "Επιλογή συνταγής...")}</option>
                  {recipes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.canonical_name} ({recipeBrandLabel(r.brand)})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {formError && (
              <div style={{ marginTop: 12, background: "#FEF0ED", border: "1px solid #FACFC8", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "var(--danger)", fontWeight: 600 }}>
                {formError}
              </div>
            )}
            <div style={{ display: "flex", gap: 9, marginTop: 20 }}>
              <button className="btn-base btn-green" style={{ flex: 1, justifyContent: "center" }} onClick={saveAlias} disabled={saving}>
                {saving ? t("Saving...", "Αποθήκευση...") : t("Save", "Αποθήκευση")}
              </button>
              <button className="btn-base btn-outline" onClick={() => { setAliasModal(null); setFormError(""); }}>
                {t("Cancel", "Ακύρωση")}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteRecipeId != null && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteRecipeId(null)}>
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-handle" />
            <div className="modal-title">{t("Delete recipe?", "Διαγραφή συνταγής;")}</div>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {t(
                "This cannot be undone. Recipes with linked aliases must be unlinked first.",
                "Η ενέργεια είναι μη αναστρέψιμη. Συνταγές με συνδεδεμένα aliases πρέπει να αποσυνδεθούν πρώτα."
              )}
            </p>
            <div style={{ display: "flex", gap: 9, marginTop: 16 }}>
              <button className="btn-base btn-green" style={{ background: "var(--danger)", borderColor: "var(--danger)" }} onClick={() => confirmDeleteRecipe(deleteRecipeId)} disabled={saving}>
                {t("Delete", "Διαγραφή")}
              </button>
              <button className="btn-base btn-outline" onClick={() => setDeleteRecipeId(null)}>
                {t("Cancel", "Ακύρωση")}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteAliasId != null && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteAliasId(null)}>
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-handle" />
            <div className="modal-title">{t("Delete alias?", "Διαγραφή alias;")}</div>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {t("This will remove the mapping between the oven program name and the canonical recipe.", "Θα αφαιρεθεί το mapping μεταξύ ονόματος προγράμματος και canonical recipe.")}
            </p>
            <div style={{ display: "flex", gap: 9, marginTop: 16 }}>
              <button className="btn-base btn-green" style={{ background: "var(--danger)", borderColor: "var(--danger)" }} onClick={() => confirmDeleteAlias(deleteAliasId)} disabled={saving}>
                {t("Delete", "Διαγραφή")}
              </button>
              <button className="btn-base btn-outline" onClick={() => setDeleteAliasId(null)}>
                {t("Cancel", "Ακύρωση")}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteInactiveAlias != null && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setDeleteInactiveAlias(null)}>
          <div className="modal-box" style={{ maxWidth: 440 }}>
            <div className="modal-handle" />
            <div className="modal-title">{t("Remove alias?", "Αφαίρεση alias;")}</div>
            <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              {t(
                `Remove alias '${deleteInactiveAlias.alias_name}'? This cannot be undone.`,
                `Αφαίρεση alias '${deleteInactiveAlias.alias_name}'; Η ενέργεια είναι μη αναστρέψιμη.`
              )}
            </p>
            <div style={{ display: "flex", gap: 9, marginTop: 16 }}>
              <button
                className="btn-base btn-green"
                style={{ background: "var(--danger)", borderColor: "var(--danger)" }}
                onClick={() => confirmDeleteInactiveAlias(deleteInactiveAlias.id)}
                disabled={saving}
              >
                {t("Delete", "Διαγραφή")}
              </button>
              <button className="btn-base btn-outline" onClick={() => setDeleteInactiveAlias(null)}>
                {t("Cancel", "Ακύρωση")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

